import React, {FC} from 'react';

import collectOpen from 'assets/v3/guide/extension/collect-open.webp';
import collectSelect from 'assets/v3/guide/extension/collect-select.webp';
import collectTranslate1 from 'assets/v3/guide/extension/collect-translate-1.webp';
import collectTranslate2 from 'assets/v3/guide/extension/collect-translate-2.webp';
import devMode from 'assets/v3/guide/extension/dev-mode.webp';
import downloadImg from 'assets/v3/guide/extension/download.webp';
import listingCollect from 'assets/v3/guide/extension/listing-collect.webp';
import listingDownload from 'assets/v3/guide/extension/listing-download.webp';
import listingOpen from 'assets/v3/guide/extension/listing-open.webp';
import listingParse from 'assets/v3/guide/extension/listing-parse.webp';
import listingPreview from 'assets/v3/guide/extension/listing-preview.webp';
import listingSelect from 'assets/v3/guide/extension/listing-select.webp';
import listingSettings from 'assets/v3/guide/extension/listing-settings.webp';
import loadUnpacked from 'assets/v3/guide/extension/load-unpacked.webp';
import pinFloat from 'assets/v3/guide/extension/pin-float.webp';
import pinToolbar from 'assets/v3/guide/extension/pin-toolbar.webp';
import refine1 from 'assets/v3/guide/extension/refine-1.webp';
import refine2 from 'assets/v3/guide/extension/refine-2.webp';
import storeInstall from 'assets/v3/guide/extension/store-install.webp';
import unzipMac from 'assets/v3/guide/extension/unzip-mac.webp';
import unzipWin from 'assets/v3/guide/extension/unzip-win.webp';
import unzippedFiles from 'assets/v3/guide/extension/unzipped-files.webp';

/**
 * Русский текст вкладки «Браузерное расширение» руководства пользователя (структура элементов 1:1 с zh.tsx).
 * AI-перевод, 2026-09-17.
 */
const RuContent: FC = () => {
  return (
    <>
      <h2>Как установить расширение в Chrome и другие браузеры?</h2>

      <h3>Способ 1: установка из Интернет-магазина Chrome</h3>
      <p>
        Для установки из официального Интернет-магазина Chrome сначала убедитесь, что ваша сеть имеет доступ к сервисам
        Google (потребуется прокси-инструмент).
      </p>
      <ul>
        <li>
          Откройте ссылку для скачивания расширения напрямую:
          <br />
          <a
            href="https://chromewebstore.google.com/detail/%E8%B1%A1%E5%AF%84%E5%9B%BE%E7%89%87%E7%BF%BB%E8%AF%91%E5%8A%A9%E6%89%8B/ejkpocbmomaefkinmkfbfnpjjfbinegb?utm_source=xj-doc"
            rel="noreferrer"
            target="_blank"
          >
            <span aria-hidden="true">🔗</span> Установить из Интернет-магазина Chrome
          </a>
        </li>
        <li>Нажмите кнопку «Установить» в правом верхнем углу, чтобы завершить установку.</li>
      </ul>
      <img alt="Установка из Интернет-магазина Chrome" decoding="async" loading="lazy" src={storeInstall} />

      <h3>Способ 2: скачайте пакет и установите вручную</h3>
      <p>
        1. Нажмите на zip-архив ниже, чтобы скачать актуальную версию расширения:
        <br />
        <a href="https://i.tosoiot.com/assets/xj-chrome-ext.zip" rel="noreferrer" target="_blank">
          <span aria-hidden="true">📥</span> Скачать браузерное расширение
        </a>
      </p>
      <p>2. После скачивания распакуйте файл. Это .zip-архив, который распаковывается в папку.</p>
      <p>macOS</p>
      <img alt="Распакованная папка на macOS" decoding="async" loading="lazy" src={unzipMac} />
      <p>Windows</p>
      <img alt="Распакованная папка на Windows" decoding="async" loading="lazy" src={unzipWin} />
      <p>Папка содержит следующие файлы:</p>
      <img alt="Содержимое папки расширения" decoding="async" loading="lazy" src={unzippedFiles} />
      <p>3. Откройте в Chrome страницу «Управление расширениями».</p>
      <ul>
        <li>Способ 1: введите chrome://extensions/ в адресную строку</li>
        <li>
          Способ 2: нажмите меню браузера в правом верхнем углу, откройте «Настройки» и найдите раздел «Расширения»
        </li>
      </ul>
      <p>Включите «Режим разработчика» в правом верхнем углу.</p>
      <img alt="Включение режима разработчика" decoding="async" loading="lazy" src={devMode} />
      <p>
        4. Нажмите «Загрузить распакованное расширение». В открывшемся диалоге выберите только что распакованную папку и
        подтвердите выбор — расширение будет добавлено в браузер.
      </p>
      <img alt="Загрузка распакованного расширения" decoding="async" loading="lazy" src={loadUnpacked} />
      <p>
        После добавления закрепите расширение на панели инструментов и включите плавающий виджет для быстрого доступа.
        Обновите страницу — и можно собирать изображения.
      </p>
      <img alt="Закрепление расширения на панели инструментов" decoding="async" loading="lazy" src={pinToolbar} />
      <img alt="Плавающий виджет" decoding="async" loading="lazy" src={pinFloat} />

      <h2>Как собирать и переводить изображения с помощью расширения?</h2>

      <h3>1. Откройте страницу товара, которую хотите перевести</h3>
      <img alt="Откройте страницу товара для перевода" decoding="async" loading="lazy" src={collectOpen} />

      <h3>2. Выберите «Перевод изображений» и соберите изображения товара</h3>
      <p>
        Нажмите значок расширения или плавающий виджет, выберите «Перевод изображений» и соберите изображения товара.
      </p>
      <img alt="Выберите тип перевода" decoding="async" loading="lazy" src={collectSelect} />
      <p>
        Отметьте изображения, которые нужно перевести, выберите целевой язык и нажмите «Перевести», чтобы начать
        обработку.
      </p>
      <img alt="Отметьте снимки товара для перевода" decoding="async" loading="lazy" src={collectTranslate1} />
      <img alt="Запуск перевода собранных снимков" decoding="async" loading="lazy" src={collectTranslate2} />

      <h3>3. Скачайте переведённые изображения или выполните ретушь</h3>
      <p>
        Нажмите на любое изображение, чтобы увеличить его и проверить перевод. Если результат устраивает, пакетно
        скачайте изображения на своё устройство.
      </p>
      <img alt="Предпросмотр и пакетное скачивание результатов" decoding="async" loading="lazy" src={downloadImg} />
      <p>Не устраивает результат? Нажмите «Ретушь», чтобы перейти на сайт Xiangji для дальнейшего редактирования.</p>
      <img alt="Нажмите «Ретушь»" decoding="async" loading="lazy" src={refine1} />
      <img alt="Ретушь на сайте Xiangji" decoding="async" loading="lazy" src={refine2} />

      <h2>Как собирать изображения товара для генерации главных и детальных изображений?</h2>

      <h3>1. Откройте страницу товара, для которой хотите сгенерировать детальные или главные изображения</h3>
      <img alt="Откройте страницу товара для генерации" decoding="async" loading="lazy" src={listingOpen} />

      <h3>2. Выберите «Генерацию детальной страницы» и соберите изображения товара</h3>
      <p>
        Нажмите значок расширения или плавающий виджет, выберите «Генерацию детальной страницы» и соберите изображения
        товара.
      </p>
      <img alt="Выберите генерацию детальной страницы" decoding="async" loading="lazy" src={listingCollect} />
      <p>
        Отметьте изображения для использования (крупные планы товара, снимки на белом фоне, снимки с разных ракурсов и
        lifestyle-снимки), затем нажмите «Генерация детальной страницы».
      </p>
      <img alt="Отметьте нужные снимки товара" decoding="async" loading="lazy" src={listingSelect} />
      <p>
        После нажатия «Генерация детальной страницы» информация о товаре и выбранные изображения распознаются
        автоматически, и через несколько секунд вы перейдёте в рабочую область Xiangji.
      </p>
      <img
        alt="Распознавание информации о товаре и переход в рабочую область"
        decoding="async"
        loading="lazy"
        src={listingParse}
      />
      <p>Заполните настройки генерации в соответствии с особенностями вашего товара.</p>
      <img alt="Заполните настройки генерации" decoding="async" loading="lazy" src={listingSettings} />
      <p>
        Через считанные секунды будет готов набор для предпросмотра, чтобы вы могли оценить стиль и эффект. Не
        устраивает? Сгенерируйте заново. Всё устраивает? Нажмите, чтобы создать финальные изображения в высоком
        разрешении.
      </p>
      <img alt="Набор для предпросмотра" decoding="async" loading="lazy" src={listingPreview} />

      <h3>3. Скачайте результаты</h3>
      <img alt="Скачивание результатов генерации" decoding="async" loading="lazy" src={listingDownload} />
    </>
  );
};

export default RuContent;
